<template>
  <div>
    <div
      v-if="businessDetailsApproved"
      class="rounded px-4 py-3 text-sm border border-green-300 bg-green-50 text-green-800 mb-6"
    >
      <div class="font-semibold mb-1">These business details have been approved.</div>
      <div class="text-xs mt-0.5">No further action required for this step.</div>
    </div>

    <div class="mb-6">
      <div class="flex items-start gap-x-3">
        <input
          id="no_trade_references"
          type="checkbox"
          class="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          v-model="form.no_trade_references"
        />
        <div class="flex-1">
          <label for="no_trade_references" class="font-medium text-sm text-gray-700">
            New Business - No Trade References
          </label>
          <div class="text-xs text-gray-500 leading-snug">
            Please note, if the trade references section is not completed, credit terms will not be extended.
            Payment terms will automatically be set to <span class="font-medium">On Receipt</span>.
          </div>
          <jet-input-error :message="$page.props.errors.no_trade_references" class="mt-1" />
        </div>
      </div>
    </div>

    <div class="grid gap-6 grid-cols-1 xl:grid-cols-4">
      <!-- General -->
      <section>
        <div class="font-semibold text-gray-800 mb-4">General</div>

        <div class="mb-4">
          <jet-label for="business_name" value="Business Name" />
          <jet-input id="business_name" type="text" class="block w-full" v-model="form.business_name" />
          <jet-input-error :message="$page.props.errors.business_name" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="phone" value="Phone" />
          <jet-input id="phone" type="tel" class="block w-full" v-model="form.phone" />
          <jet-input-error :message="$page.props.errors.phone" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="business_contact" value="Business Contact" />
          <jet-input id="business_contact" type="text" class="block w-full" v-model="form.business_contact" />
          <jet-input-error :message="$page.props.errors.business_contact" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="business_email" value="Business Email" />
          <jet-input id="business_email" type="email" class="block w-full" v-model="form.business_email" />
          <jet-input-error :message="$page.props.errors.business_email" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="years_in_business" value="Years in Business" />
          <jet-input
            id="years_in_business"
            type="number"
            min="0"
            step="1"
            class="block w-full"
            v-model="form.years_in_business"
          />
          <jet-input-error :message="$page.props.errors.years_in_business" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="contact_name" value="Accounts Payable Contact" />
          <jet-input id="contact_name" type="text" class="block w-full" v-model="form.contact_name" />
          <jet-input-error :message="$page.props.errors.contact_name" class="mt-1" />
        </div>

        <div class="mb-1">
          <jet-label for="ap_email" value="Accounts Payable Email" />
          <jet-input id="ap_email" type="email" class="block w-full" v-model="form.ap_email" />
          <jet-input-error :message="$page.props.errors.ap_email" class="mt-1" />
        </div>
      </section>

      <!-- Address -->
      <section>
        <div class="font-semibold text-gray-800 mb-4">Address</div>

        <div class="mb-4">
          <jet-label for="address_1" value="Address" />
          <jet-input id="address_1" type="text" class="block w-full" v-model="form.address_1" />
          <jet-input-error :message="$page.props.errors.address_1" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="city" value="City" />
          <jet-input id="city" type="text" class="block w-full" v-model="form.city" />
          <jet-input-error :message="$page.props.errors.city" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="province" value="Province" />
          <multiselect
            v-model="form.province"
            :options="provinceOptions"
            :classes="{ singleLabel: 'w-full px-2' }"
            class="multi-lg"
            placeholder="Select province"
          />
          <jet-input-error :message="$page.props.errors.province" class="mt-1" />
        </div>

        <div class="mb-1">
          <jet-label for="postal_code" value="Postal Code" />
          <jet-input id="postal_code" type="text" class="block w-full" v-model="form.postal_code" />
          <jet-input-error :message="$page.props.errors.postal_code" class="mt-1" />
        </div>
      </section>

      <!-- rade Reference #1 -->
      <section v-show="showTradeReferences">
        <div class="font-semibold text-gray-800 mb-4">Trade Reference #1</div>

        <div class="mb-4">
          <jet-label for="tr1_company_name" value="Company Name" />
          <jet-input
            id="tr1_company_name"
            type="text"
            class="block w-full"
            v-model="form.trade_reference_1.company_name"
          />
          <jet-input-error :message="$page.props.errors['trade_reference_1.company_name']" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="tr1_company_address" value="Company Address" />
          <jet-input
            id="tr1_company_address"
            type="text"
            class="block w-full"
            v-model="form.trade_reference_1.company_address"
          />
          <jet-input-error :message="$page.props.errors['trade_reference_1.company_address']" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="tr1_contact_name" value="Contact Name" />
          <jet-input
            id="tr1_contact_name"
            type="text"
            class="block w-full"
            v-model="form.trade_reference_1.contact_name"
          />
          <jet-input-error :message="$page.props.errors['trade_reference_1.contact_name']" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="tr1_phone" value="Phone" />
          <jet-input id="tr1_phone" type="tel" class="block w-full" v-model="form.trade_reference_1.phone" />
          <jet-input-error :message="$page.props.errors['trade_reference_1.phone']" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="tr1_email" value="Email" />
          <jet-input id="tr1_email" type="email" class="block w-full" v-model="form.trade_reference_1.email" />
          <jet-input-error :message="$page.props.errors['trade_reference_1.email']" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="tr1_account_opened_since" value="Account Opened Since" />
          <jet-input
            id="tr1_account_opened_since"
            type="text"
            class="block w-full"
            v-model="form.trade_reference_1.account_opened_since"
            placeholder="e.g., 2021 or Jan 2021"
          />
          <jet-input-error :message="$page.props.errors['trade_reference_1.account_opened_since']" class="mt-1" />
        </div>

        <div class="mb-1">
          <jet-label for="tr1_current_balance" value="Current Balance" />
          <jet-input
            id="tr1_current_balance"
            type="text"
            class="block w-full"
            v-model="form.trade_reference_1.current_balance"
            placeholder="$0.00"
          />
          <jet-input-error :message="$page.props.errors['trade_reference_1.current_balance']" class="mt-1" />
        </div>
      </section>

      <!-- Trade Reference #2 -->
      <section v-show="showTradeReferences">
        <div class="font-semibold text-gray-800 mb-4">Trade Reference #2</div>

        <div class="mb-4">
          <jet-label for="tr2_company_name" value="Company Name" />
          <jet-input
            id="tr2_company_name"
            type="text"
            class="block w-full"
            v-model="form.trade_reference_2.company_name"
          />
          <jet-input-error :message="$page.props.errors['trade_reference_2.company_name']" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="tr2_company_address" value="Company Address" />
          <jet-input
            id="tr2_company_address"
            type="text"
            class="block w-full"
            v-model="form.trade_reference_2.company_address"
          />
          <jet-input-error :message="$page.props.errors['trade_reference_2.company_address']" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="tr2_contact_name" value="Contact Name" />
          <jet-input
            id="tr2_contact_name"
            type="text"
            class="block w-full"
            v-model="form.trade_reference_2.contact_name"
          />
          <jet-input-error :message="$page.props.errors['trade_reference_2.contact_name']" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="tr2_phone" value="Phone" />
          <jet-input id="tr2_phone" type="tel" class="block w-full" v-model="form.trade_reference_2.phone" />
          <jet-input-error :message="$page.props.errors['trade_reference_2.phone']" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="tr2_email" value="Email" />
          <jet-input id="tr2_email" type="email" class="block w-full" v-model="form.trade_reference_2.email" />
          <jet-input-error :message="$page.props.errors['trade_reference_2.email']" class="mt-1" />
        </div>

        <div class="mb-4">
          <jet-label for="tr2_account_opened_since" value="Account Opened Since" />
          <jet-input
            id="tr2_account_opened_since"
            type="text"
            class="block w-full"
            v-model="form.trade_reference_2.account_opened_since"
            placeholder="e.g., 2021 or Jan 2021"
          />
          <jet-input-error :message="$page.props.errors['trade_reference_2.account_opened_since']" class="mt-1" />
        </div>

        <div class="mb-1">
          <jet-label for="tr2_current_balance" value="Current Balance" />
          <jet-input
            id="tr2_current_balance"
            type="text"
            class="block w-full"
            v-model="form.trade_reference_2.current_balance"
            placeholder="$0.00"
          />
          <jet-input-error :message="$page.props.errors['trade_reference_2.current_balance']" class="mt-1" />
        </div>
      </section>
    </div>

    <!-- Payment Preferences -->
    <div class="mt-8 grid gap-6 grid-cols-1 xl:grid-cols-4">
      <div class="xl:col-span-1">
        <div class="font-semibold text-gray-800 mb-4">Payment Preferences</div>

        <div class="mb-6">
          <jet-label for="payment_method" value="Payment Method" class="whitespace-nowrap" />
          <multiselect
            v-model="form.payment_method"
            :options="paymentMethodOptions"
            :classes="{ singleLabel: 'w-full px-2' }"
            :canClear="false"
            :canDeselect="false"
            placeholder="Select method"
            class="multi-lg"
          />
          <jet-input-error :message="$page.props.errors.payment_method" class="mt-1" />
        </div>

        <div class="mb-1">
          <jet-label for="payment_terms" value="Payment Terms" class="whitespace-nowrap" />
          <multiselect
            v-model="form.payment_terms"
            :options="paymentTermsOptions"
            :classes="{ singleLabel: 'w-full px-2' }"
            :canClear="false"
            :canDeselect="false"
            :disabled="form.no_trade_references"
            placeholder="Select terms"
            class="multi-lg"
          />
          <jet-input-error :message="$page.props.errors.payment_terms" class="mt-1" />
        </div>
      </div>

      <div class="hidden xl:block xl:col-span-3"></div>
    </div>

    <div v-if="form.payment_method === 'exception'" class="mt-4">
      <jet-label for="exception_notes" value="Exception Notes" />
      <textarea
        id="exception_notes"
        v-model="form.exception_notes"
        rows="4"
        class="w-full border rounded p-2 text-sm"
        placeholder="Explain why Exception was selected (e.g. e-transfer, indirect billing, special arrangement)…"
      />
      <jet-input-error :message="$page.props.errors.exception_notes" class="mt-1" />
    </div>

    <div class="mt-8">
      <div class="mt-8 grid gap-6 grid-cols-1 xl:grid-cols-2">
        <div class="xl:col-span-1">
          <div class="font-semibold text-gray-800 mb-2">Authorization &amp; Consent</div>

          <div class="text-sm text-gray-600 leading-relaxed mb-4">
              I hereby certify that the information contained herein is complete and accurate. Furthermore, 
              I hereby authorize the trade references listed in this credit application to release necessary
              information to the company for which credit is being applied for in order to verify the 
              information contained herein. I authorize Produce Express Inc. to process payment based on the 
              agreed upon credit terms with the selected payment method.
          </div>

          <div class="mb-4">
              <jet-label for="signature_name" value="Signed By" />
              <jet-input id="signature_name" type="text" class="block w-full" v-model="form.signature_name" />
              <jet-input-error :message="$page.props.errors.signature_name" class="mt-1" />
          </div>
        </div>
      </div>

      <signature-pad
          title="Signature"
          type="customer_business_details"
          :object="form"
          object_property_name_filename="signature_filename"
          object_property_name_date="signature_last_updated"
          :can_redo="true"
          :filename="form.signature_filename"
      />

      <div
        v-if="signatureNeedsConfirm"
        class="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2"
      >
        Please sign and click <span class="font-semibold">Confirm</span> to save your signature before submitting.
      </div>

      <jet-input-error :message="$page.props.errors.signature_filename" class="mt-1" />
      </div>
  </div>
</template>

<script>
import JetLabel from '@/Jetstream/Label'
import JetInput from '@/Jetstream/Input'
import JetInputError from '@/Jetstream/InputError.vue'
import Multiselect from '@vueform/multiselect'
import SignaturePad from '@/Shared/SignaturePad.vue'

export default {
  props: [
    'form',
    'customer',
    'prefill',
    'onboardingStatus',
    'paymentTermsOptions',
    'paymentMethodOptions',
  ],

  components: {
    JetLabel,
    JetInput,
    JetInputError,
    Multiselect,
    SignaturePad,
  },

  data() {
    return {
      provinceOptions: [
        'Ontario',
        'Manitoba',
        'Saskatchewan',
        'British Columbia',
        'Yukon',
        'Northwest Territories',
        'Nunavut',
        'Quebec',
        'Nova Scotia',
        'New Brunswick',
        'Newfoundland',
        'Prince Edward Island',
      ],
    }
  },

  computed: {
    showTradeReferences() {
      return !this.form.no_trade_references
    },

    signatureNeedsConfirm() {
      const hasName = !!this.form.signature_name
      const hasFilename = !!this.form.signature_filename
      const serverError = !!this.$page.props?.errors?.signature_filename

      return (hasName && !hasFilename) || serverError
    },

    businessDetailsApproved() {
      return new Set([
        'business_details_approved',
        'payment_details_submitted',
        'payment_details_correction_required',
        'payment_details_approved',
      ]).has(this.onboardingStatus)
    },
  },

  watch: {
    'form.no_trade_references': {
      immediate: true,
      handler(isNoTradeRefs) {
        if (isNoTradeRefs) {
          this.form.payment_terms = 'on_receipt'
          this.clearTradeReferences()
        }
      },
    },

    'form.payment_method': {
      immediate: true,
      handler(method) {
        if (method !== 'exception') this.form.exception_notes = null
      },
    },

    paymentMethodOptions: {
      immediate: true,
      handler(options) {
        const allowed = options ? Object.keys(options) : []
        if (this.form.payment_method && !allowed.includes(this.form.payment_method)) {
          this.form.payment_method = null
          this.form.exception_notes = null
        }
      },
    },
  },

  methods: {
    clearTradeReferences() {
      if (this.form.trade_reference_1) {
        this.form.trade_reference_1 = {
          company_name: null,
          company_address: null,
          contact_name: null,
          phone: null,
          email: null,
          account_opened_since: null,
          current_balance: null,
        }
      }

      if (this.form.trade_reference_2) {
        this.form.trade_reference_2 = {
          company_name: null,
          company_address: null,
          contact_name: null,
          phone: null,
          email: null,
          account_opened_since: null,
          current_balance: null,
        }
      }
    },
  },
}
</script>

<style scoped>
.multiselect.is-active {
    box-shadow: 0 0 0 2px rgba(133, 199, 30, 0.4) !important;
}
</style>
